/* eslint-disable import/prefer-default-export */
import { HistoryAction, HistoryCategory, VehicleUnitType } from 'constants/vehicles';
import { Record, RecordOf } from 'immutable';
import {
  Vehicle,
  VehicleModel,
  Damage,
  DamagePicture,
  vehicleStatus,
  VehicleHistoryUpdate,
  VehicleHistoryNote,
  VehicleHistoryUser as VehicleHistoryUserType,
  VehicleHistoryVehicle as VehicleHistoryVehicleType,
  VehicleHistoryAgreement,
  VehicleHistoryGeofence,
  VehicleHistoryUnit,
} from 'services/resources';

const VehicleModelRecord = Record<VehicleModel>({
  id: undefined,
  make: undefined,
  model: undefined,
  year: undefined,
  fuelTankCapacity: undefined,
  fuelType: undefined,
});

const VehicleDamagePictureRecord = Record<DamagePicture>({
  id: undefined,
  image: undefined,
  imageThumbnail: undefined,
  addedBy: undefined,
  addedDate: undefined,
  damage: undefined,
  name: undefined,
  imageMedium: undefined,
});

const VehicleDamageRecord = Record<Damage>({
  comment: undefined,
  dateCreated: undefined,
  dateModified: undefined,
  id: undefined,
  part: undefined,
  pictures: undefined,
  source: undefined,
  vehicle: undefined,
});

export const VehicleRecord = Record<Vehicle>({
  agreements: undefined,
  availability: undefined,
  batteryVoltage: undefined,
  body: undefined,
  customer: undefined,
  damages: [],
  dealership: undefined,
  department: undefined,
  dmsLastUpdated: undefined,
  dtcs: undefined,
  fuelLevel: undefined,
  fuelLevelStale: undefined,
  fuelUnit: undefined,
  id: undefined,
  ignition: undefined,
  interiorColor: undefined,
  lastEventDate: undefined,
  latitude: undefined,
  licensePlate: undefined,
  longitude: undefined,
  mil: undefined,
  model: VehicleModelRecord({
    id: undefined,
    make: undefined,
    model: undefined,
    year: undefined,
    fuelTankCapacity: undefined,
    fuelType: undefined,
  }),
  nextAgreementId: undefined,
  nextAgreementExpectedDateOut: undefined,
  odometer: undefined,
  odometerInitial: undefined,
  odometerInitialDate: undefined,
  odometerStale: undefined,
  odometerUnit: undefined,
  owner: undefined,
  status: vehicleStatus.AVAILABLE,
  stockNumber: undefined,
  tagNumber: undefined,
  vin: undefined,
  addedToFleetBy: {
    id: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
  },
  addedToFleetDate: undefined,
  batteryLevel: undefined,
  combinedFuelLevel: undefined,
  combinedFuelLevelType: undefined,
  combinedOdometer: undefined,
  combinedOdometerType: undefined,
  connectedBatteryLevel: undefined,
  connectedBatteryLevelDate: undefined,
  connectedChargingState: undefined,
  connectedChargingStateDate: undefined,
  connectedFuelLevel: undefined,
  connectedFuelLevelDate: undefined,
  connectedOdometer: undefined,
  connectedOdometerDate: undefined,
  exteriorColor: undefined,
  imei: undefined,
  isConnected: undefined,
  locationDate: undefined,
  removedFromFleetBy: {
    id: undefined,
    firstName: undefined,
    lastName: undefined,
    email: undefined,
  },
  removedFromFleetDate: undefined,
  reportedOdometer: undefined,
  reportedOdometerDate: undefined,
  unitType: VehicleUnitType.LOANER,
});

type UserVehicleRecords = {
  user?: RecordOf<VehicleHistoryUserType>;
  vehicle: RecordOf<VehicleHistoryVehicleType>;
};

// Converting user and vehicles into RecordOf types, since that's how they're stored.
export type VehicleHistoryUpdateType = Omit<VehicleHistoryUpdate, 'user' | 'vehicle'> & UserVehicleRecords;
export type VehicleHistoryNoteType = Omit<VehicleHistoryNote, 'user' | 'vehicle'> & UserVehicleRecords;
export type VehicleHistoryAgreementType = Omit<VehicleHistoryAgreement, 'user' | 'vehicle'> & UserVehicleRecords;
export type VehicleHistoryGeofenceType = Omit<VehicleHistoryGeofence, 'user' | 'vehicle'> & UserVehicleRecords;
export type VehicleHistoryUnitType = Omit<VehicleHistoryUnit, 'user' | 'vehicle'> & UserVehicleRecords;

// Building RecordOf types for all Historical record types.
export type VehicleHistoryUpdateRecord = RecordOf<VehicleHistoryUpdateType>;
export type VehicleHistoryNoteRecord = RecordOf<VehicleHistoryNoteType>;
export type VehicleHistoryAgreementRecord = RecordOf<VehicleHistoryAgreementType>;
export type VehicleHistoryGeofenceRecord = RecordOf<VehicleHistoryGeofenceType>;
export type VehicleHistoryUnitRecord = RecordOf<VehicleHistoryUnitType>;

export type VehicleHistoryRecord =
  | VehicleHistoryUpdateRecord
  | VehicleHistoryNoteRecord
  | VehicleHistoryAgreementRecord
  | VehicleHistoryGeofenceRecord
  | VehicleHistoryUnitRecord;

export const VehicleHistoryUser = Record<VehicleHistoryUserType>({
  id: 0,
  firstName: '',
  lastName: '',
  email: '',
});

export const VehicleHistoryVehicle = Record<VehicleHistoryVehicleType>({
  status: vehicleStatus.AVAILABLE,
  vin: '',
});

export const VehicleHistoryDetailsChangeRecord = Record<VehicleHistoryUpdateType>({
  data: [],
  category: HistoryCategory.DETAILS,
  action: HistoryAction.UPDATED,
  objectId: undefined,
  id: 0,
  dateCreated: '',
  user: VehicleHistoryUser(),
  vehicle: VehicleHistoryVehicle(),
});

export const VehicleHistoryNoteAddedRecord = Record<VehicleHistoryNoteType>({
  data: {
    id: 0,
    note: '',
  },
  category: HistoryCategory.NOTE,
  action: HistoryAction.NOTE_ADDED,
  objectId: 0,
  id: 0,
  dateCreated: '',
  user: VehicleHistoryUser(),
  vehicle: VehicleHistoryVehicle(),
});

export const VehicleHistoryGeofenceBreachRecord = Record<VehicleHistoryGeofenceType>({
  category: HistoryCategory.GEOFENCE,
  action: HistoryAction.ENTERED,
  data: {
    name: '',
    id: 0,
  },
  objectId: null,
  id: 0,
  dateCreated: '',
  user: VehicleHistoryUser(),
  vehicle: VehicleHistoryVehicle(),
});

export const VehicleHistoryAgreementChangeRecord = Record<VehicleHistoryAgreementType>({
  category: HistoryCategory.AGREEMENT,
  action: HistoryAction.ADDED,
  data: [],
  objectId: 0,
  id: 0,
  dateCreated: '',
  user: VehicleHistoryUser(),
  vehicle: VehicleHistoryVehicle(),
});

export const VehicleHistoryUnitTypeChangeRecord = Record<VehicleHistoryUnitType>({
  category: HistoryCategory.UNIT_TYPE,
  action: HistoryAction.SET_LOANER,
  data: [],
  objectId: null,
  id: 0,
  dateCreated: '',
  user: VehicleHistoryUser(),
  vehicle: VehicleHistoryVehicle(),
});
